import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

// A second copy of the token vocabulary, pinned by a test against the names
// actually registered in `@theme`.
export const TOKEN_CLASS_GROUPS = {
  spacing: [
    'inline',
    'field-label',
    'group',
    'field',
    'section',
    'page',
    'page-x',
    'page-y',
    'card',
    'dialog',
    'sidebar',
    'button',
    'button-group',
    'input',
    'dropdown-item-x',
    'dropdown-item-y',
    'tooltip-x',
    'tooltip-y',
    'header-x',
    'nav-y',
    'badge-x',
    'badge-y',
    'switch',
    'tab-x',
    'tab-y',
    'alert-x',
    'alert-y',
    'scrollbar-inset',
    'focus-ring',
  ],
  text: [
    'hero',
    'display',
    'page-title',
    'section-title',
    'card-title',
    'title',
    'body',
    'caption',
    'badge',
    'overline',
  ],
  radius: ['drawer'],
  shadow: [
    'inset',
    'surface',
    'surface-hover',
    'card',
    'card-hover',
    'raised',
    'overlay',
    'dialog',
  ],
  ease: ['emphasized'],
  animate: [
    'accordion-down',
    'accordion-up',
    'collapsible-down',
    'collapsible-up',
    'fade-in',
    'fade-out',
    'slide-in-from-top',
    'slide-in-from-bottom',
    'slide-in-from-left',
    'slide-in-from-right',
    'drawer-in-left',
    'drawer-in-right',
    'drawer-in-top',
    'drawer-in-bottom',
    'drawer-out-left',
    'drawer-out-right',
    'drawer-out-top',
    'drawer-out-bottom',
    'zoom-in',
    'zoom-out',
    'spin',
  ],
  height: [
    'control',
    'control-sm',
    'control-lg',
    'control-xl',
    'drawer',
    'drawer-sm',
    'drawer-lg',
    'drawer-xl',
    'drawer-full',
    'layout',
    'layout-sm',
    'layout-lg',
    'layout-xl',
    'nav',
    'menu',
    'switch',
    'slider-track',
    'scrollbar',
    'scrollbar-thumb',
    'tab-indicator',
  ],
  width: ['switch', 'sidebar-collapsed', 'scrollbar', 'scrollbar-thumb'],
  size: [
    'icon',
    'icon-xs',
    'icon-sm',
    'icon-lg',
    'icon-xl',
    'icon-2xl',
    'control',
    'control-sm',
    'control-lg',
    'control-xl',
    'checkbox',
    'radio',
    'switch-thumb',
    'slider-thumb',
  ],
  borderWidth: ['surface', 'control'],
  container: [
    'panel',
    'panel-sm',
    'panel-lg',
    'drawer',
    'drawer-sm',
    'drawer-lg',
    'drawer-xl',
    'drawer-full',
    'grid-narrow',
  ],
  zIndex: ['dropdown', 'sticky', 'fixed', 'overlay', 'modal', 'popover', 'tooltip', 'toast'],
  duration: ['fastest', 'faster', 'fast', 'normal', 'slow', 'slower', 'slowest'],
} as const;

const g = TOKEN_CLASS_GROUPS;

// `theme` reaches every group built on a scale; the five below are not, so they
// take an explicit group each.
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      spacing: [...g.spacing],
      text: [...g.text],
      radius: [...g.radius],
      shadow: [...g.shadow],
      ease: [...g.ease],
      animate: [...g.animate],
    },
    classGroups: {
      h: [{ h: [...g.height] }],
      w: [{ w: [...g.width] }],
      size: [{ size: [...g.size] }],
      'border-w': [{ border: [...g.borderWidth] }],
      // Both scales share this prefix: --container-* sizes a box's own max-width (panel,
      // drawer), --width-* sizes a part of one (the scrollbar thumb) — tailwind-merge only
      // needs to know they collide on the same CSS property, not which scale either came from.
      'max-w': [{ 'max-w': [...g.container, ...g.width] }],
      'max-h': [{ 'max-h': [...g.height] }],
      z: [{ z: [...g.zIndex] }],
      duration: [{ duration: [...g.duration] }],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
